/**
 * EquipmentLayoutEditor.jsx — A.6 Equipment Layout Editor for Process Plant.
 *
 * Interactive plot plan editor that lets users:
 *   • View the auto-generated preliminary equipment layout
 *   • Adjust plot dimensions (width × height)
 *   • See OISD-118 spacing compliance in real-time
 *   • View hazardous area zones (API 505)
 *   • Download the layout as SVG
 *   • Re-run with modified parameters
 *
 * INTEGRATION:
 *   File: frontend/src/components/EquipmentLayoutEditor.jsx
 *
 *   In StagePanel.jsx, add this import:
 *     import EquipmentLayoutEditor from "./EquipmentLayoutEditor";
 *
 *   Then in the tabBuilder section (around line 469), add:
 *     if (stage.code === "A.6") built.push({
 *       key: "equipment-layout",
 *       label: "Equipment Layout",
 *       Icon: Grid3X3,
 *       render: () => <EquipmentLayoutEditor projectId={projectId} />
 *     });
 *
 * DEPENDENCIES: Uses only standard React hooks + the existing api utility.
 *               No additional npm packages required.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  RefreshCw, Download, CheckCircle, AlertTriangle,
  ZoomIn, ZoomOut, Maximize2, Grid, Eye, EyeOff,
} from "lucide-react";

// Use the existing api utility from the Capexara Process Industry platform
// import api from "../lib/api";

/**
 * EquipmentLayoutEditor — main component.
 *
 * Props:
 *   projectId (string)  — current project ID
 *   stageOutput (object) — optional: pre-loaded stage output with computed.svg_drawing
 *   onRerun (function)  — optional: callback to trigger re-run of A.6 engine
 */
export default function EquipmentLayoutEditor({
  projectId,
  stageOutput = null,
  onRerun = null,
}) {
  const [svgContent, setSvgContent] = useState(stageOutput?.computed?.svg_drawing || "");
  const [plotWidth, setPlotWidth] = useState(stageOutput?.computed?.plot_width_m || 100);
  const [plotHeight, setPlotHeight] = useState(stageOutput?.computed?.plot_height_m || 50);
  const [nEquipment, setNEquipment] = useState(stageOutput?.computed?.n_equipment || 0);
  const [spacingStatus, setSpacingStatus] = useState(stageOutput?.computed?.spacing_status || "");
  const [violations, setViolations] = useState(stageOutput?.computed?.violation_details || []);
  const [showZones, setShowZones] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const containerRef = useRef(null);

  // Load from stage output if available
  useEffect(() => {
    if (stageOutput?.computed) {
      const c = stageOutput.computed;
      setSvgContent(c.svg_drawing || "");
      setPlotWidth(c.plot_width_m || 100);
      setPlotHeight(c.plot_height_m || 50);
      setNEquipment(c.n_equipment || 0);
      setSpacingStatus(c.spacing_status || "");
      setViolations(c.violation_details || []);
    }
  }, [stageOutput]);

  // Re-run engine with new parameters
  const handleRerun = useCallback(async () => {
    if (!onRerun) return;
    setLoading(true);
    setError("");
    try {
      await onRerun({
        plot_width_m: plotWidth,
        plot_height_m: plotHeight,
      });
    } catch (e) {
      setError(e.message || "Failed to regenerate layout");
    } finally {
      setLoading(false);
    }
  }, [onRerun, plotWidth, plotHeight]);

  // Download SVG
  const handleDownload = useCallback(() => {
    if (!svgContent) return;
    const blob = new Blob([svgContent], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `A6_Equipment_Layout_${projectId}.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [svgContent, projectId]);

  return (
    <div style={containerStyle}>
      {/* Header bar */}
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={titleStyle}>
            A.6 — Preliminary Equipment Layout
          </div>
          <span style={badgeStyle}>
            {nEquipment} equipment
          </span>
          {spacingStatus && (
            <span style={{
              ...badgeStyle,
              background: spacingStatus === "PASS"
                ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
              color: spacingStatus === "PASS" ? "#22c55e" : "#ef4444",
            }}>
              {spacingStatus === "PASS" ? (
                <><CheckCircle size={12} /> Spacing OK</>
              ) : (
                <><AlertTriangle size={12} /> {violations.length} violations</>
              )}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button type="button" onClick={() => setShowZones(!showZones)}
            style={toolBtnStyle} title={showZones ? "Hide zones" : "Show zones"}>
            {showZones ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          <button type="button" onClick={() => setShowGrid(!showGrid)}
            style={toolBtnStyle} title="Toggle grid">
            <Grid size={14} />
          </button>
          <button type="button" onClick={() => setZoom(z => Math.min(z + 0.25, 3))}
            style={toolBtnStyle} title="Zoom in">
            <ZoomIn size={14} />
          </button>
          <button type="button" onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))}
            style={toolBtnStyle} title="Zoom out">
            <ZoomOut size={14} />
          </button>
          <button type="button" onClick={() => setZoom(1)}
            style={toolBtnStyle} title="Reset zoom">
            <Maximize2 size={14} />
          </button>
          <div style={{ width: 1, height: 20, background: "var(--pf-border, #334155)" }} />
          <button type="button" onClick={handleDownload}
            disabled={!svgContent}
            style={{ ...toolBtnStyle, color: "var(--pf-spark, #0ea5e9)" }}
            title="Download SVG">
            <Download size={14} />
          </button>
          {onRerun && (
            <button type="button" onClick={handleRerun}
              disabled={loading}
              style={{ ...toolBtnStyle, color: "#22c55e" }}
              title="Regenerate layout">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
          )}
        </div>
      </div>

      {/* Parameters bar */}
      <div style={paramsBarStyle}>
        <label style={paramLabelStyle}>
          Plot Width (m):
          <input type="number" value={plotWidth}
            onChange={e => setPlotWidth(Number(e.target.value) || 100)}
            style={paramInputStyle} min={20} max={500} step={10} />
        </label>
        <label style={paramLabelStyle}>
          Plot Height (m):
          <input type="number" value={plotHeight}
            onChange={e => setPlotHeight(Number(e.target.value) || 50)}
            style={paramInputStyle} min={20} max={300} step={10} />
        </label>
        <span style={{ fontSize: 11, color: "var(--pf-text-muted, #94a3b8)" }}>
          Area: {(plotWidth * plotHeight).toLocaleString()} m²
          {plotWidth * plotHeight > 0 && (
            <> · {((plotWidth * plotHeight) / 10000).toFixed(2)} ha</>
          )}
        </span>
        {error && (
          <span style={{ fontSize: 11, color: "#ef4444", marginLeft: "auto" }}>
            {error}
          </span>
        )}
      </div>

      {/* SVG viewer */}
      <div ref={containerRef} style={viewerStyle}>
        {svgContent ? (
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
              transition: "transform 0.2s ease",
            }}
            dangerouslySetInnerHTML={{ __html: svgContent }}
          />
        ) : (
          <div style={emptyStateStyle}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📐</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
              No Equipment Layout Generated Yet
            </div>
            <div style={{ fontSize: 12, maxWidth: 400, lineHeight: 1.6 }}>
              Run Stage A.6 to generate a preliminary equipment layout.
              The engine reads your Design Basis (A.1) to auto-place
              equipment with OISD-118 safety spacing and API 505
              hazardous area zones.
            </div>
            {onRerun && (
              <button type="button" onClick={handleRerun}
                style={generateBtnStyle} disabled={loading}>
                {loading ? "Generating..." : "Generate Layout"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Violations panel (shown only if violations exist) */}
      {violations.length > 0 && (
        <div style={violationsPanelStyle}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#ef4444",
            marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <AlertTriangle size={14} />
            OISD-118 Spacing Violations ({violations.length})
          </div>
          {violations.map((v, i) => (
            <div key={i} style={violationRowStyle}>
              <span style={{ fontFamily: "monospace", fontSize: 11 }}>{v}</span>
            </div>
          ))}
          <div style={{ fontSize: 10, color: "var(--pf-text-muted, #94a3b8)",
            marginTop: 8, fontStyle: "italic" }}>
            Review equipment positions to resolve spacing violations.
            Refer to OISD-118 Table 3 for minimum inter-equipment distances.
          </div>
        </div>
      )}

      {/* Standards reference footer */}
      <div style={footerStyle}>
        <span>Safety Spacing: OISD-118 / NFPA 30 / API 2510</span>
        <span>Hazardous Area: API 505 / IEC 60079-10-1</span>
        <span>Plot Plan: IS 4576 / NFPA 30</span>
      </div>
    </div>
  );
}


/* ────────────── Styles ───────────────────────────────────── */

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  background: "var(--pf-bg, #0f172a)",
  borderRadius: 8,
  overflow: "hidden",
  border: "1px solid var(--pf-border, #1e293b)",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 16px",
  background: "var(--pf-surface, #1e293b)",
  borderBottom: "1px solid var(--pf-border, #334155)",
  flexWrap: "wrap",
  gap: 8,
};

const titleStyle = {
  fontSize: 13,
  fontWeight: 700,
  color: "var(--pf-text, #e2e8f0)",
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  fontWeight: 600,
  padding: "2px 8px",
  borderRadius: 999,
  background: "rgba(14,165,233,0.12)",
  color: "#0ea5e9",
};

const toolBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: 4,
  border: "1px solid var(--pf-border, #334155)",
  background: "transparent",
  color: "var(--pf-text-muted, #94a3b8)",
  cursor: "pointer",
  padding: 0,
};

const paramsBarStyle = {
  display: "flex",
  alignItems: "center",
  gap: 16,
  padding: "8px 16px",
  background: "var(--pf-surface, #1e293b)",
  borderBottom: "1px solid var(--pf-border, #334155)",
  flexWrap: "wrap",
};

const paramLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 11,
  color: "var(--pf-text-muted, #94a3b8)",
  fontWeight: 600,
};

const paramInputStyle = {
  width: 70,
  padding: "3px 6px",
  fontSize: 12,
  borderRadius: 4,
  border: "1px solid var(--pf-border, #334155)",
  background: "var(--pf-bg, #0f172a)",
  color: "var(--pf-text, #e2e8f0)",
  fontFamily: "monospace",
};

const viewerStyle = {
  flex: 1,
  minHeight: 400,
  overflow: "auto",
  padding: 16,
  background: "white",
};

const emptyStateStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 350,
  color: "var(--pf-text-muted, #94a3b8)",
  textAlign: "center",
};

const generateBtnStyle = {
  marginTop: 16,
  padding: "10px 24px",
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 6,
  border: "none",
  background: "#0d9488",
  color: "white",
  cursor: "pointer",
};

const violationsPanelStyle = {
  padding: "10px 16px",
  background: "rgba(239,68,68,0.06)",
  borderTop: "1px solid rgba(239,68,68,0.2)",
};

const violationRowStyle = {
  padding: "3px 0",
  borderBottom: "1px solid rgba(239,68,68,0.1)",
};

const footerStyle = {
  display: "flex",
  justifyContent: "space-between",
  padding: "6px 16px",
  fontSize: 10,
  color: "var(--pf-text-muted, #64748b)",
  background: "var(--pf-surface, #1e293b)",
  borderTop: "1px solid var(--pf-border, #334155)",
  flexWrap: "wrap",
  gap: 8,
};
